import {
  afterNextRender,
  Component,
  ElementRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { ToastService, ToastNotification } from '@core/toast';
import { M3IconButtonComponent } from '@shared/button/icon-button.component';
import { M3ButtonComponent } from '@shared/button/m3-button.component';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [M3IconButtonComponent, M3ButtonComponent],
  host: {
    role: 'status',
    'aria-live': 'polite',
    'aria-atomic': 'false',
  },
  template: `
    <div class="toast-container">
      @for (toast of toastService.toasts$(); track toast.id) {
        <div
          class="toast-item"
          [class]="getToastClasses(toast)"
          [class.toast-item--leaving]="leavingToasts().has(toast.id)"
          [class.toast-item--paused]="pausedToasts().has(toast.id)"
          [attr.role]="toast.type === 'error' ? 'alert' : null"
          [attr.aria-live]="toast.type === 'error' ? 'assertive' : null"
          (mouseenter)="pause(toast.id)"
          (mouseleave)="resumeAfterLeave(toast.id, $event)"
          (focusin)="pause(toast.id)"
          (focusout)="resumeAfterLeave(toast.id, $event)"
        >
          <div class="toast-content">
            <div class="toast-icon" [class]="getIconClasses(toast)">
              <span class="material-symbols-outlined" aria-hidden="true">{{
                getIconName(toast.type)
              }}</span>
            </div>

            <div class="toast-text">
              <h4 class="toast-title" [class]="getTitleClasses(toast)">
                {{ toast.title }}
              </h4>
              <p class="toast-message" [class]="getMessageClasses(toast)">
                {{ toast.message }}
              </p>

              @if (toast.actions && toast.actions.length > 0) {
                <div class="toast-actions">
                  @for (action of toast.actions; track action.label) {
                    <app-m3-button
                      variant="tonal"
                      size="sm"
                      (clicked)="action.action(); dismissToast(toast.id)"
                    >
                      {{ action.label }}
                    </app-m3-button>
                  }
                </div>
              }
            </div>

            <app-m3-icon-button
              icon="close"
              variant="standard"
              size="sm"
              ariaLabel="Toast schließen"
              (clicked)="dismissToast(toast.id)"
            />
          </div>

          @if (toast.duration > 0) {
            <div class="progress-bar" [class]="getProgressBarClasses(toast)">
              <div
                class="progress-fill"
                [class]="getProgressFillClasses(toast)"
                [style.animation-duration.ms]="toast.duration"
              ></div>
            </div>
          }
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .toast-container {
        position: fixed;
        bottom: 16px;
        right: 16px;
        z-index: 1100;
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-width: 384px;
        width: 100%;
        pointer-events: none;
      }

      .toast-item {
        pointer-events: auto;
        background: var(--md-sys-color-surface-container-highest);
        color: var(--md-sys-color-on-surface);
        border-radius: var(--md-sys-shape-corner-medium);
        box-shadow:
          0 2px 6px 2px rgba(0, 0, 0, 0.15),
          0 1px 2px 0 rgba(0, 0, 0, 0.3);
        border: 1px solid var(--md-sys-color-outline-variant);
        overflow: hidden;
        animation: toast-slide-in var(--md-comp-toast-motion-enter);

        @starting-style {
          opacity: 0;
          transform: translateX(100%) scale(0.85);
        }
      }

      .toast-item--paused .progress-fill {
        animation-play-state: paused;
      }

      .toast-item--leaving {
        opacity: 0;
        transform: translateX(100%) scale(0.85);
        transition:
          opacity var(--md-comp-toast-motion-exit),
          transform var(--md-comp-toast-motion-exit);
      }

      .toast-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
      }

      .toast-icon {
        flex-shrink: 0;
        margin-top: 2px;
      }

      .toast-icon .material-symbols-outlined {
        font-size: 20px;
        font-variation-settings:
          'FILL' 1,
          'wght' 400,
          'GRAD' 0,
          'opsz' 20;
      }

      .toast-text {
        flex: 1;
        min-width: 0;
      }

      .toast-title {
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        margin: 0 0 4px 0;
      }

      .toast-message {
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        margin: 0;
      }

      .toast-actions {
        margin-top: 12px;
        display: flex;
        gap: 8px;
      }

      .progress-bar {
        width: 100%;
        height: 4px;
        overflow: hidden;
      }

      .progress-fill {
        height: 100%;
        animation-name: progress;
        animation-timing-function: linear;
        animation-fill-mode: forwards;
        transition: all var(--md-sys-motion-standard-effects-default);
      }

      @keyframes progress {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }

      // Success toast styling
      .toast-item.success {
        border-color: var(--md-extended-color-success-color);
      }

      .toast-icon.success .material-symbols-outlined {
        color: var(--md-extended-color-success-color);
      }

      .toast-title.success {
        color: var(--md-sys-color-on-surface);
      }

      .toast-message.success {
        color: var(--md-sys-color-on-surface-variant);
      }

      .progress-bar.success {
        background: var(--md-extended-color-success-color-container);
      }

      .progress-fill.success {
        background: var(--md-extended-color-success-color);
      }

      // Error toast styling
      .toast-item.error {
        border-color: var(--md-sys-color-error);
      }

      .toast-icon.error .material-symbols-outlined {
        color: var(--md-sys-color-error);
      }

      .toast-title.error {
        color: var(--md-sys-color-on-surface);
      }

      .toast-message.error {
        color: var(--md-sys-color-on-surface-variant);
      }

      .progress-bar.error {
        background: var(--md-sys-color-error-container);
      }

      .progress-fill.error {
        background: var(--md-sys-color-error);
      }

      // Warning toast styling
      .toast-item.warning {
        border-color: var(--md-sys-color-tertiary);
      }

      .toast-icon.warning .material-symbols-outlined {
        color: var(--md-sys-color-tertiary);
      }

      .toast-title.warning {
        color: var(--md-sys-color-on-surface);
      }

      .toast-message.warning {
        color: var(--md-sys-color-on-surface-variant);
      }

      .progress-bar.warning {
        background: var(--md-sys-color-tertiary-container);
      }

      .progress-fill.warning {
        background: var(--md-sys-color-tertiary);
      }

      // Info toast styling
      .toast-item.info {
        border-color: var(--md-extended-color-info-color);
      }

      .toast-icon.info .material-symbols-outlined {
        color: var(--md-extended-color-info-color);
      }

      .toast-title.info {
        color: var(--md-sys-color-on-surface);
      }

      .toast-message.info {
        color: var(--md-sys-color-on-surface-variant);
      }

      .progress-bar.info {
        background: var(--md-extended-color-info-color-container);
      }

      .progress-fill.info {
        background: var(--md-extended-color-info-color);
      }
    `,
  ],
})
export class ToastContainerComponent {
  toastService = inject(ToastService);
  leavingToasts = signal<Set<string>>(new Set());
  pausedToasts = signal<Set<string>>(new Set());

  constructor() {
    // A dialog sets aria-hidden on every child of <body> except its overlay and live regions.
    // Inside <app-root> the toasts would be hidden from screen readers while a dialog is open.
    const host = inject(ElementRef<HTMLElement>).nativeElement;
    const doc = inject(DOCUMENT);
    afterNextRender(() => doc.body.appendChild(host));
  }

  pause(id: string): void {
    this.toastService.pause(id);
    this.pausedToasts.update((set) => new Set([...set, id]));
  }

  resumeAfterLeave(id: string, event: MouseEvent | FocusEvent): void {
    const toast = event.currentTarget as HTMLElement;
    const stillFocused =
      event instanceof FocusEvent
        ? toast.contains(event.relatedTarget as Node | null)
        : toast.contains(document.activeElement);
    const stillHovered = event instanceof FocusEvent && toast.matches(':hover');
    if (stillFocused || stillHovered) return;

    this.toastService.resume(id);
    this.pausedToasts.update((set) => {
      const newSet = new Set(set);
      newSet.delete(id);
      return newSet;
    });
  }

  dismissToast(id: string): void {
    // Add to leaving set to trigger exit animation
    this.leavingToasts.update((set) => new Set([...set, id]));

    // Wait for animation to complete, then dismiss
    setTimeout(() => {
      this.toastService.dismiss(id);
      this.pausedToasts.update((set) => {
        const newSet = new Set(set);
        newSet.delete(id);
        return newSet;
      });
      this.leavingToasts.update((set) => {
        const newSet = new Set(set);
        newSet.delete(id);
        return newSet;
      });
    }, 200); // Match exit animation duration
  }

  getToastClasses(toast: ToastNotification): string {
    return toast.type;
  }

  getIconClasses(toast: ToastNotification): string {
    return toast.type;
  }

  getIconName(type: string): string {
    switch (type) {
      case 'success':
        return 'check_circle';
      case 'error':
        return 'cancel';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  }

  getTitleClasses(toast: ToastNotification): string {
    return toast.type;
  }

  getMessageClasses(toast: ToastNotification): string {
    return toast.type;
  }

  getProgressBarClasses(toast: ToastNotification): string {
    return toast.type;
  }

  getProgressFillClasses(toast: ToastNotification): string {
    return toast.type;
  }
}
